import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react"

import { MAX_TAGS } from "../constants/tag-constraints"
import {
  getSharedTags,
  resolveBulkTagUpdates,
  type TaggableRecord,
} from "../utils/bulkTags"
import { normalizeTags } from "../utils/tagNormalization"
import {
  beginTagWrite,
  forgetSettledTagWrite,
  getTagWriteSnapshot,
  retainTagWrites,
  subscribeToTagWrites,
  tagWriteKey,
} from "../utils/tagWriteLedger"
import { enqueueTagWrite } from "../utils/tagWriteQueue"

interface UseBulkTaggingParams {
  /** Invalidation for the surfaces that read the rewritten records. */
  invalidateTagged: (ids: string[]) => Promise<void>
  /** Rows for the selected records the list is currently carrying. */
  loadedRecords: readonly TaggableRecord[]
  /** Every selected record, including any whose row has left the list. */
  recordIds: readonly string[]
  /**
   * The resource the selected records belong to, e.g. `"agents"`. Namespaces
   * the per-record write queue and ledger, since ids are only unique within a
   * resource.
   */
  resource: string
  /** Writes a record's whole tag list. */
  updateTags: (id: string, tags: string[]) => Promise<unknown>
}

interface BulkTaggingOutcome {
  failedCount: number
  /** Records left untouched because they already hold the maximum tags. */
  saturatedCount: number
  updatedCount: number
}

interface BulkTaggingResult {
  applyTags: (nextTags: string[]) => Promise<BulkTaggingOutcome>
  sharedTags: string[]
}

const NO_CHANGES: BulkTaggingOutcome = {
  failedCount: 0,
  saturatedCount: 0,
  updatedCount: 0,
}

/**
 * Bulk tag assignment over a selection of records, whatever resource they are.
 *
 * The rows are the authority on what a record is tagged with, but they lag: a
 * write only shows up once the list refetches, and a write that moves a record
 * out of the active filter drops its row from the list altogether. So what a
 * record's writes have set takes precedence over its row until the row catches
 * up, which is what lets a second edit diff against something real rather than
 * against a list the first edit has already replaced.
 *
 * That record of the writes is kept outside the component, because a selection
 * bar unmounts the moment the selection is cleared while its writes are still
 * outstanding, and a record can leave the selection and rejoin it before the
 * list reflects the first edit.
 *
 * A record's writes are queued behind each other, so a second edit reaching a
 * record while its first one is still in flight cannot land out of order and
 * leave the record holding the older list.
 */
export function useBulkTagging({
  invalidateTagged,
  loadedRecords,
  recordIds,
  resource,
  updateTags,
}: UseBulkTaggingParams): BulkTaggingResult {
  const writtenTagsByKey = useSyncExternalStore(
    subscribeToTagWrites,
    getTagWriteSnapshot,
    getTagWriteSnapshot,
  )

  const rowTagsById = useMemo(
    () =>
      new Map(
        loadedRecords.map((record) => [
          record.id,
          normalizeTags(record.tags ?? [], MAX_TAGS),
        ]),
      ),
    [loadedRecords],
  )

  useEffect(() => {
    for (const id of recordIds) {
      const rowTags = rowTagsById.get(id)
      if (rowTags) {
        forgetSettledTagWrite(resource, id, rowTags)
      }
    }
  }, [recordIds, resource, rowTagsById])

  // The selection is a live reader of every record in it, so the ledger's
  // overflow eviction leaves those records alone however many other records a
  // session has edited — an evicted one would send the next edit back to a row
  // that predates the write the entry was remembering.
  useEffect(() => {
    return retainTagWrites(resource, recordIds)
  }, [recordIds, resource])

  const targetRecords = useMemo(
    () =>
      recordIds.flatMap((id) => {
        const tags =
          writtenTagsByKey.get(tagWriteKey(resource, id)) ?? rowTagsById.get(id)
        return tags ? [{ id, tags }] : []
      }),
    [recordIds, resource, rowTagsById, writtenTagsByKey],
  )

  const sharedTags = useMemo(
    () => getSharedTags(targetRecords),
    [targetRecords],
  )

  const applyTags = useCallback(
    async (nextTags: string[]): Promise<BulkTaggingOutcome> => {
      const { saturatedIds, updates } = resolveBulkTagUpdates({
        maxTags: MAX_TAGS,
        nextSharedTags: nextTags,
        previousSharedTags: sharedTags,
        records: targetRecords,
      })

      if (updates.length === 0) {
        return { ...NO_CHANGES, saturatedCount: saturatedIds.length }
      }

      const settledResults = await Promise.allSettled(
        updates.map((update) => {
          const write = beginTagWrite(resource, update.id, update.tags)

          return enqueueTagWrite(resource, update.id, async () => {
            try {
              const result = await updateTags(update.id, update.tags)
              write.settle(true)
              return result
            } catch (error) {
              write.settle(false)
              throw error
            }
          })
        }),
      )

      const succeededIds: string[] = []
      const failedIds: string[] = []
      updates.forEach((update, index) => {
        if (settledResults[index]?.status === "fulfilled") {
          succeededIds.push(update.id)
        } else {
          failedIds.push(update.id)
        }
      })

      await invalidateTagged(updates.map(({ id }) => id))

      return {
        failedCount: failedIds.length,
        saturatedCount: saturatedIds.length,
        updatedCount: succeededIds.length,
      }
    },
    [invalidateTagged, resource, sharedTags, targetRecords, updateTags],
  )

  return { applyTags, sharedTags }
}
